import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { Dialog, Toolbar } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { UserAndUserGroupOverviews, UserAndUserGroupSelection } from "./UserAndUserGroupOverviews";
import { ActiveTab } from "../UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";

export type SelectUserAndUserGroupDialogProps = {
	title: string;
	initialActiveTab: ActiveTab;
	data: UsersAndUserGroupsDataModel;
	onSelected(selection: UserAndUserGroupSelection, lastActiveTab: ActiveTab): void;
	onClose(): void;
	hasBusyIndicator?: boolean;
	requiredUserLicense?: UserLicense | null;
};

export const SelectUserAndUserGroupDialog = (props: SelectUserAndUserGroupDialogProps): ReactElement => {
	const {
		title,
		initialActiveTab,
		data,
		onSelected,
		onClose,
		hasBusyIndicator = false,
		requiredUserLicense
	} = props;

	const selectableData: UsersAndUserGroupsDataModel = useMemo(
		() => {
			return {
				userGroups: data.userGroups,
				users: data.users.filter(x => !x.deletionDate)
			};
		},
		[data]
	);

	const [activeTab, setActiveTab] = useState<ActiveTab>(initialActiveTab);
	const [selection, setSelection] = useState<UserAndUserGroupSelection>({
		users: [],
		userGroups: []
	});

	const addHandler = (): void => {
		onSelected(
			selection,
			activeTab
		);
	};

	return (
		<Dialog
			trackingId="select-user-and-group-dialog"
			size="large"
			hasBusyIndicator={hasBusyIndicator}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
			/>
			<Dialog.Body>
				<UserAndUserGroupOverviews
					activeTab={activeTab}
					onActiveTabChanged={setActiveTab}
					data={selectableData}
					selection={selection}
					onSelectionChange={setSelection}
					requiredUserLicense={requiredUserLicense}
				/>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="add"
						kind="primary"
						text={localize("_Add")}
						onClick={addHandler}
						isDisabled={selection.users.length === 0 && selection.userGroups.length === 0}
					/>

					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SelectUserAndUserGroupDialog.displayName = "SelectUserAndUserGroupDialog";
